import DomainChecker from '../index';

export interface DomainSuggestions {
    prefix: string;
    suffixes: string[];
}

export interface Price {
    period: number;
    price: string;
    originalPrice: string;
}

export type Pricing = Price[];

export interface DomainResponse {
    currency: string;
    taxLabel: string;
    isAvailable: boolean;
    formattedPrice: string;
    formattedYearlyPrice: string | null;
    success: boolean;
    error: string;
    pricing: Pricing;
    domainSuggestions: DomainSuggestions;
}

/**
 * In-flight and completed lookups keyed by locale, suggestion limit and domain.
 * Repeat searches within a page session are answered without another round trip,
 * and a domain that appears in several result lists is only requested once.
 * Failed or unsuccessful lookups are evicted so they can be retried.
 */
const lookupCache = new Map<string, Promise<DomainResponse>>();

export const clearFetchCache = (): void => {
    lookupCache.clear();
};

const isAbortError = (err: unknown): boolean =>
    typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AbortError';

const requestDomain = async (domainName: string, locale: string, limit: number, signal?: AbortSignal): Promise<DomainResponse> => {
    const url = window.location.hostname.includes('rocketspark.com') ? DomainChecker.PRODUCTION_FETCH : DomainChecker.DEVELOPMENT_FETCH;
    try {
        const rsKey = new URLSearchParams(location.search).get('rsKey');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                countryCode: locale,
                domain: domainName,
                rsKey: rsKey,
                suggestionsLimit: limit.toString()
            }),
            signal,
        });
        return response.json();
    } catch (err) {
        if (!isAbortError(err)) {
            console.error(err);
        }
        throw err;
    }
};

export const FetchDomain = (domainName: string, locale: string, limit = 5, signal?: AbortSignal): Promise<DomainResponse> => {
    const key = `${locale}|${limit}|${domainName}`;
    const cached = lookupCache.get(key);
    if (cached) {
        return cached;
    }

    const request = requestDomain(domainName, locale, limit, signal);
    lookupCache.set(key, request);
    request
        .then(response => {
            if (!response || !response.success) {
                lookupCache.delete(key);
            }
        })
        .catch(() => {
            lookupCache.delete(key);
        });
    return request;
};
